import React, { useState, useEffect, useCallback } from 'react';
import { api } from './utils/api';
import CommuteMap from './components/CommuteMap';
import CommuteList from './components/CommuteList';
import SegmentCreator from './components/SegmentCreator';
import SegmentsList from './components/SegmentsList';
import SegmentLeaderboard from './components/SegmentLeaderboard';
import { Segment } from './types';

function App() {
  const [username, setUsername] = useState<string>('');
  const [userId, setUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activePage, setActivePage] = useState<'commutes' | 'segments' | 'leaderboards'>('commutes');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [headerUsername, setHeaderUsername] = useState<string>('');
  const [selectedCommute, setSelectedCommute] = useState<number | null>(null);
  const [timeFilter, setTimeFilter] = useState<'today' | 'week' | 'all'>('today');
  const [segments, setSegments] = useState<Segment[]>([]);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [segmentsError, setSegmentsError] = useState<string | null>(null);
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null);

  const handleUsernameSubmit = useCallback(async (newUsername: string) => {
    setUsername(newUsername);
    setUserId(null);
    
    try {
      setLoading(true);
      setError(null);
      const user = await api.getUser(newUsername);
      setUserId(user.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch user');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSegments = useCallback(async () => {
    try {
      setSegmentsLoading(true);
      setSegmentsError(null);
      const fetchedSegments = await api.getSegments();
      setSegments(fetchedSegments);
    } catch (err) {
      setSegmentsError(err instanceof Error ? err.message : 'Failed to fetch segments');
    } finally {
      setSegmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check for saved theme preference or default to dark mode
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldUseDark = savedTheme === 'dark' || (!savedTheme && prefersDark);
    
    setIsDarkMode(shouldUseDark);
    if (shouldUseDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Load cached username and auto-submit if available
    const cachedUsername = localStorage.getItem('commute-viewer-username');
    if (cachedUsername && !username) {
      handleUsernameSubmit(cachedUsername);
    }
  }, [handleUsernameSubmit, username]);

  useEffect(() => {
    if (activePage === 'leaderboards' && segments.length === 0) {
      fetchSegments();
    }
  }, [activePage, segments.length, fetchSegments]);

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('theme', newDarkMode ? 'dark' : 'light');
    
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleHeaderUsernameSubmit = async () => {
    if (headerUsername.trim()) {
      const newUsername = headerUsername.trim();
      // Cache the username
      localStorage.setItem('commute-viewer-username', newUsername);
      await handleUsernameSubmit(newUsername);
      setHeaderUsername('');
    }
  };

  const handleCommuteSelect = (commuteId: number) => {
    setSelectedCommute(commuteId === selectedCommute ? null : commuteId);
  };

  const handleSegmentClick = (segment: Segment) => {
    setSelectedSegment(segment);
  };

  const handleBackToSegments = () => {
    setSelectedSegment(null);
  };

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-900">
      <header className="bg-white dark:bg-stone-800 shadow-sm border-b dark:border-stone-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Commute</h1>
              <p className="text-gray-600 dark:text-gray-300 mt-1">Visualize your driving commutes on a map</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Username field and button */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={headerUsername}
                  onChange={(e) => setHeaderUsername(e.target.value)}
                  placeholder={username || "Username"}
                  disabled={loading}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                  onKeyPress={(e) => e.key === 'Enter' && handleHeaderUsernameSubmit()}
                />
                <button
                  onClick={handleHeaderUsernameSubmit}
                  disabled={!headerUsername.trim() || loading}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Loading...' : 'Go'}
                </button>
              </div>
              
              {/* Theme toggle button */}
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? (
                  <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="mt-4 flex">
            <button
              onClick={() => setActivePage('commutes')}
              className={`py-2 px-4 mr-12 border-b-2 font-medium text-sm ${
                activePage === 'commutes'
                  ? 'border-stone-600 text-stone-700 dark:text-stone-300 dark:border-stone-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500'
              }`}
            >
              Commutes
            </button>
            <button
              onClick={() => setActivePage('segments')}
              className={`py-2 px-4 mr-12 border-b-2 font-medium text-sm ${
                activePage === 'segments'
                  ? 'border-stone-600 text-stone-700 dark:text-stone-300 dark:border-stone-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500'
              }`}
            >
              Segments
            </button>
            <button
              onClick={() => setActivePage('leaderboards')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activePage === 'leaderboards'
                  ? 'border-stone-600 text-stone-700 dark:text-stone-300 dark:border-stone-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500'
              }`}
            >
              Leaderboards
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {activePage === 'segments' ? (
          <div className="space-y-6">
            <SegmentCreator />
            <SegmentsList />
          </div>
        ) : activePage === 'leaderboards' ? (
          selectedSegment ? (
            <SegmentLeaderboard
              segmentId={selectedSegment.id!}
              segmentName={selectedSegment.name}
              onBack={handleBackToSegments}
            />
          ) : (
            <div className="space-y-6">
              {/* About Leaderboards */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-blue-900 font-medium mb-2">About Leaderboards</h3>
                <p className="text-blue-800 text-sm">
                  Leaderboards show the fastest completion times for each segment across all users. 
                  Results are ranked by duration, with the fastest times at the top. 
                  Each result includes the user, completion time, date, and commute ID.
                </p>
              </div>

              {/* Select a Segment */}
              <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow">
                <div className="p-4 border-b">
                  <h2 className="text-xl font-semibold text-gray-900">Select a Segment</h2>
                  <p className="text-gray-600 text-sm mt-1">
                    Click on a segment to view its leaderboard and see how users perform
                  </p>
                </div>

                {segmentsLoading && (
                  <div className="p-6">
                    <div className="h-64 flex items-center justify-center">
                      <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <div className="text-lg mt-4 text-gray-600 dark:text-gray-300">Loading segments...</div>
                      </div>
                    </div>
                  </div>
                )}

                {segmentsError && (
                  <div className="p-6">
                    <div className="h-64 flex items-center justify-center">
                      <div className="text-red-600 text-lg">Error: {segmentsError}</div>
                    </div>
                  </div>
                )}

                {!segmentsLoading && !segmentsError && (
                  <div className="p-6">
                    {segments.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <div className="text-lg mb-2">No segments found</div>
                        <p className="text-sm">Create some segments first to see leaderboards</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {segments.map((segment) => (
                          <div
                            key={segment.id}
                            className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md cursor-pointer transition-all duration-200 bg-gradient-to-br from-white to-gray-50"
                            onClick={() => handleSegmentClick(segment)}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <h3 className="font-semibold text-gray-900 text-lg">
                                {segment.name}
                              </h3>
                              <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                ID: {segment.id}
                              </div>
                            </div>
                            
                            <div className="space-y-2 text-sm text-gray-600">
                              <div className="flex justify-between">
                                <span>Start:</span>
                                <span className="font-mono">
                                  {segment.start_lat.toFixed(4)}, {segment.start_lon.toFixed(4)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>End:</span>
                                <span className="font-mono">
                                  {segment.end_lat.toFixed(4)}, {segment.end_lon.toFixed(4)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Radius:</span>
                                <span>
                                  {segment.radius_start || 50}m → {segment.radius_end || 50}m
                                </span>
                              </div>
                            </div>
                            
                            <div className="mt-4 pt-3 border-t border-gray-200">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500">Click to view leaderboard</span>
                                <div className="text-blue-600 text-sm font-medium">
                                  View Results →
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        ) : (
          <>
            {loading && (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  <div className="text-lg mt-4 text-gray-600 dark:text-gray-300">Loading user...</div>
                </div>
              </div>
            )}

            {error && (
              <div className="text-center py-8">
                <div className="text-red-600 text-lg">Error: {error}</div>
              </div>
            )}

            {userId && !loading && (
              <div className="space-y-6">
                <CommuteMap 
                  userId={userId} 
                  selectedCommute={selectedCommute}
                  onCommuteSelect={handleCommuteSelect}
                  timeFilter={timeFilter}
                  onTimeFilterChange={setTimeFilter}
                />
                <CommuteList 
                  userId={userId} 
                  selectedCommute={selectedCommute}
                  onCommuteSelect={handleCommuteSelect}
                  timeFilter={timeFilter}
                />
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default App;
